import { useEffect, useRef, useState } from "react";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { SeverityPill, SEVERITY_PILL_CLASS } from "admin/components/diagnostics/SeverityPill";
import {
  SEVERITY_INDICATOR_CONFIG,
  getFailureAppliesToLabel,
  getTriageStatusConfig,
} from "admin/types/constants/diagnostics";
import type { DiagnosticListItem } from "admin/types/interfaces/diagnostics";
import { scoreColor } from "admin/utils/scoring";
import { Activity } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { useTurnDiagnostics } from "./TurnDiagnosticsContext";

interface TurnDiagnosticIndicatorProps {
  /** The turn's anchor id (root trace id). `undefined` while the turn is still running. */
  turnId: string | undefined;
}

/**
 * Grace period before a pointer leaving the icon closes the peek. The card is
 * portalled, so it is not a descendant of the trigger: without the delay the
 * card unmounts as the pointer crosses the gap and `Open detail` is
 * unreachable by mouse.
 */
const PEEK_CLOSE_DELAY_MS = 150;

const INDICATOR_CLASS = "grid size-4 shrink-0 place-items-center rounded";

/**
 * The per-turn diagnostics affordance in the transcript: a severity-tinted
 * activity icon beside the agent label, with a hover/focus peek card. Renders
 * nothing wherever no `TurnDiagnosticsProvider` is mounted, which is every
 * surface except the admin conversation view with diagnostics enabled.
 */
export const TurnDiagnosticIndicator = ({ turnId }: TurnDiagnosticIndicatorProps) => {
  const context = useTurnDiagnostics();
  const [isPeekOpen, setIsPeekOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (closeTimer.current != null) clearTimeout(closeTimer.current);
    },
    []
  );

  if (!context || !turnId) return null;

  const { byTurnId, ordinalByTurnId, totalTurns, selectTurn } = context;
  const diagnostic = byTurnId.get(turnId);
  const ordinal = ordinalByTurnId.get(turnId);
  const indicator = SEVERITY_INDICATOR_CONFIG[diagnostic?.severity ?? "unscored"];
  const turnPosition = ordinal != null ? `Turn ${ordinal} of ${totalTurns}` : null;

  const cancelScheduledClose = () => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openPeek = () => {
    cancelScheduledClose();
    setIsPeekOpen(true);
  };

  const closePeek = () => {
    cancelScheduledClose();
    setIsPeekOpen(false);
  };

  const closePeekAfterDelay = () => {
    cancelScheduledClose();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = null;
      setIsPeekOpen(false);
    }, PEEK_CLOSE_DELAY_MS);
  };

  const handleOpenDetail = () => {
    closePeek();
    selectTurn(turnId);
  };

  const peekHandlers = {
    onMouseEnter: openPeek,
    onMouseLeave: closePeekAfterDelay,
  };

  const icon = (
    <Activity
      size={11}
      strokeWidth={2.2}
      strokeDasharray={diagnostic ? undefined : "3 3"}
      aria-hidden="true"
    />
  );

  const score = diagnostic?.averageScore ?? null;
  const scorePhrase = score != null ? `score ${score.toFixed(2)} of 5` : "no rubric score";

  return (
    <Popover
      open={isPeekOpen}
      // Hover and focus own opening; Radix only reports Escape and outside clicks.
      onOpenChange={(open) => !open && closePeek()}
    >
      {diagnostic ? (
        <PopoverTrigger asChild>
          <button
            type="button"
            {...peekHandlers}
            onFocus={openPeek}
            onBlur={closePeek}
            onClick={handleOpenDetail}
            aria-label={`${indicator.label} severity, ${scorePhrase}. Open turn diagnostics.`}
            className={cn(
              INDICATOR_CLASS,
              "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              indicator.iconButtonClass
            )}
          >
            {icon}
          </button>
        </PopoverTrigger>
      ) : (
        // Anchor rather than Trigger: an unscored turn has no detail to open, and
        // Trigger would stamp button semantics (`type`, `aria-expanded`) onto a
        // span that only reveals a hover card.
        <PopoverAnchor asChild>
          <span
            {...peekHandlers}
            role="img"
            aria-label={indicator.label}
            className={cn(INDICATOR_CLASS, indicator.iconButtonClass)}
          >
            {icon}
          </span>
        </PopoverAnchor>
      )}
      <PopoverContent
        {...peekHandlers}
        align="start"
        alignOffset={-8}
        sideOffset={6}
        aria-label="Turn diagnostics summary"
        // A hover peek must never pull focus off the transcript.
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn("flex flex-col gap-2.5 p-3", diagnostic ? "w-[330px]" : "w-[280px]")}
      >
        {diagnostic ? (
          <ScoredPeek
            diagnostic={diagnostic}
            turnPosition={turnPosition}
            onOpenDetail={handleOpenDetail}
          />
        ) : (
          <UnscoredPeek turnPosition={turnPosition} />
        )}
      </PopoverContent>
    </Popover>
  );
};

const TurnPosition = ({ position }: { position: string | null }) => {
  if (!position) return null;
  return <span className="ml-auto text-xs text-muted-foreground">{position}</span>;
};

interface ScoredPeekProps {
  diagnostic: DiagnosticListItem;
  turnPosition: string | null;
  onOpenDetail: () => void;
}

const ScoredPeek = ({ diagnostic, turnPosition, onOpenDetail }: ScoredPeekProps) => {
  const score = diagnostic.averageScore;

  return (
    <>
      <div className="flex items-center gap-2">
        <SeverityPill severity={diagnostic.severity} />
        <span className={cn("text-sm font-semibold tabular-nums", scoreColor(score))}>
          {score != null ? score.toFixed(2) : "N/A"}
          <span className="ml-1 text-xs font-medium text-muted-foreground">/ 5</span>
        </span>
        <TurnPosition position={turnPosition} />
      </div>
      <div className="flex items-center gap-1.5 text-xs">
        <span className="text-muted-foreground">Failure area</span>
        <span className="font-medium">{getFailureAppliesToLabel(diagnostic.primaryFailure)}</span>
      </div>
      {diagnostic.failureSummary && (
        // A teaser, not the whole finding: the rail this card opens shows the
        // summary in full, and an unclamped paragraph makes the peek tower over
        // the turn it belongs to.
        <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
          {diagnostic.failureSummary}
        </p>
      )}
      <div className="flex items-center gap-2 border-t pt-2.5">
        <Button size="sm" className="h-7 px-2.5 text-xs" onClick={onOpenDetail}>
          Open detail
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {getTriageStatusConfig(diagnostic.triageStatus).label}
        </span>
      </div>
    </>
  );
};

const UnscoredPeek = ({ turnPosition }: { turnPosition: string | null }) => (
  <>
    <div className="flex items-center gap-2">
      <span className={cn(SEVERITY_PILL_CLASS, "bg-muted text-muted-foreground")}>
        {SEVERITY_INDICATOR_CONFIG.unscored.label}
      </span>
      <TurnPosition position={turnPosition} />
    </div>
    <p className="text-xs leading-relaxed text-muted-foreground">
      Diagnostics have not run for this turn yet.
    </p>
  </>
);
